import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faTrash,
  faEdit,
  faSave,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
// Импортируем ваш глобальный компонент модального окна
import Modal from "../../components/modal/modal";

const ViewVacancie = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  // Получаем списки опций динамически из i18next (как в AddVacancy)
  const industriesObj = t("industries", { returnObjects: true }) || {};
  const INDUSTRIES = Object.entries(industriesObj).map(([key, value]) => ({ key, value }));

  // Базовые опции для графиков и типов занятости
  const getTypeOptions = () => [
    { value: "Permanent", label: t("Permanent") },
    { value: "Temporary", label: t("Temporary") },
    { value: "Contract", label: t("Contract") },
    { value: "Other", label: t("Other") },
  ];

  const getRateOptions = () => [
    { value: "Full-time", label: t("Full-time") },
    { value: "Part-time", label: t("Part-time") },
    { value: "Other", label: t("Other") },
  ];

  const getExperienceOptions = () => [
    { value: "no", label: t("no") },
    { value: "beginner", label: t("beginner") },
    { value: "mid", label: t("mid") },
    { value: "senior", label: t("senior") },
    { value: "undefined", label: t("undefined") },
  ];

  // --- ОПРЕДЕЛЯЕМ МАССИВЫ ДЛЯ ИСПРАВЛЕНИЯ ОШИБОК ИНИЦИАЛИЗАЦИИ ---
  const WORKPLACES = [
    { key: "remote", label: t("remote") || "Удаленная работа" },
  ];

  const EMPLOYMENT_TYPES = [
    { key: "Permanent", label: t("Permanent") },
    { key: "Temporary", label: t("Temporary") },
    { key: "Contract", label: t("Contract") },
    { key: "Other", label: t("Other") },
  ];

  const RATE_TYPES = [
    { key: "Full-time", label: t("Full-time") },
    { key: "Part-time", label: t("Part-time") },
    { key: "Other", label: t("Other") },
  ];

  const EXPERIENCE_OPTIONS = [
    { key: "no", label: t("no") },
    { key: "beginner", label: t("beginner") },
    { key: "mid", label: t("mid") },
    { key: "senior", label: t("senior") },
    { key: "undefined", label: t("undefined") },
  ];

  const currencyOptions = [
    { value: "AMD", label: "֏", flag: "🇦🇲" },
    { value: "USD", label: "$", flag: "🇺🇸" },
    { value: "EUR", label: "€", flag: "🇪🇺" },
    { value: "RUB", label: "₽", flag: "🇷🇺" },
  ];

  const CURRENCIES = ["AMD", "USD", "EUR", "RUB"];

  // Безопасное получение данных пользователя
  const userData = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const userType = userData.role || "guest";
  const userId = userData.id || localStorage.getItem("userId");

  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similarVacancies, setSimilarVacancies] = useState([]);
  const [copied, setCopied] = useState(false);

  // Режим редактирования
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Состояние модального окна в стиле EditCompanyPage
  const [modalMessage, setModalMessage] = useState(null);

  // Состояния для формы отклика
  const [responseForm, setResponseForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Хелпер для парсинга JSON
  const safeJSON = (str) => {
    try {
      if (!str) return [];
      return typeof str === "string" ? JSON.parse(str) : str;
    } catch {
      return [];
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchVacancy = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/vacancyview.php?id=${id}&role=${userType}`, { 
            cache: "no-cache" 
        });
        
        const data = await response.json();

        if (data.success && isMounted) {
          setVacancy({
            ...data.vacancy,
            responsibilities: safeJSON(data.vacancy.responsibilities),
            requirements: safeJSON(data.vacancy.requirements),
            conditions: safeJSON(data.vacancy.conditions),
            considerAlso: safeJSON(data.vacancy.considerAlso),
            views: data.views || 0,
            responses: data.responses || 0,
          });
          if (data.similar) setSimilarVacancies(data.similar);
        } else {
          console.error("Server error:", data.message);
        }
      } catch (error) {
        console.error("Failed to fetch vacancy:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchVacancy();
    return () => { isMounted = false; };
  }, [id, userType]);

  const imageUrls = useMemo(() => ({
    background: vacancy?.background ? `/api/uploads/${vacancy.background}` : null,
    logo: vacancy?.companyLogo ? `/api/uploads/${vacancy.companyLogo}` : null,
  }), [vacancy]);

  const handleCopyId = () => {
    if (vacancy?.id) {
      navigator.clipboard.writeText(vacancy.id.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setResponseForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setResponseForm(prev => ({ ...prev, resume: e.target.files[0] }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setVacancy(prev => ({ ...prev, [name]: value }));
  };

  // Обработчик изменения чекбокса remote
  const handleRemoteToggle = (e) => {
    const isRemote = e.target.checked;
    setVacancy(prev => ({
      ...prev,
      workplace: isRemote ? t("remote") : "",
      workplaceAddress: isRemote ? "" : prev.workplaceAddress || ""
    }));
  };

  // Обработчик изменения адреса
  const handleAddressChange = (e) => {
    setVacancy(prev => ({
      ...prev,
      workplaceAddress: e.target.value
    }));
  };

  const handleListChange = (blockName, index, value) => {
    setVacancy(prev => {
      const updatedList = [...prev[blockName]];
      updatedList[index] = value;
      return { ...prev, [blockName]: updatedList };
    });
  };

  const addListItem = (blockName) => {
    setVacancy(prev => ({
      ...prev,
      [blockName]: [...prev[blockName], ""]
    }));
  };

  const removeListItem = (blockName, index) => {
    setVacancy(prev => ({
      ...prev,
      [blockName]: prev[blockName].filter((_, idx) => idx !== index)
    }));
  };

  // Сохранение отредактированных данных
  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const updateData = {
        ...vacancy,
        responsibilities: JSON.stringify(vacancy.responsibilities),
        requirements: JSON.stringify(vacancy.requirements),
        conditions: JSON.stringify(vacancy.conditions),
        considerAlso: JSON.stringify(vacancy.considerAlso),
      };

      const response = await fetch("/api/update_vacancy.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();
      if (data.success) {
        setIsEditing(false);
        setModalMessage({
            title: t("success"),
          text: t("data_saved"),
          success: true,
          ok: t("ok"),
        });
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error("Error updating vacancy:", error);
      setModalMessage({
        title: t("error"),
        text: t("server_error") || "Ошибка сервера",
        success: false,
        ok: t("ok"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!vacancy?.id || !userId) {
        setModalMessage({
          title: t("error"),
          text: t("allfields") || "Заполните все поля",
          success: false,
          ok: t("ok"),
        });
        return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formData = new FormData();
      formData.append("vacancy_id", vacancy.id);
      formData.append("user_id", userId);
      formData.append("company_id", vacancy.user_id);
      formData.append("vacancy_title", vacancy.workname);
      formData.append("name", responseForm.name);
      formData.append("email", responseForm.email);
      formData.append("phone", responseForm.phone);
      formData.append("message", responseForm.message);
      if (responseForm.resume) formData.append("resume", responseForm.resume);

      const response = await fetch("/api/submit_response.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        setSubmitStatus({ type: "success", message: "response_sent" });
        setResponseForm({ name: "", email: "", phone: "", message: "", resume: null });
        setModalMessage({
          title: t("success") || "Успешно",
          text: t("response_sent"),
          success: true,
          ok: t("ok"),
        });
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      setSubmitStatus({ type: "error", message: "errortitle" });
      setModalMessage({
        title: t("error"),
        text: t("errortitle"),
        success: false,
        ok: t("ok"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-[500px] flex justify-center items-center">
        <p className="text-[#006674] text-xl font-semibold animate-pulse">
          {t("loading")}...
        </p>
      </div>
    );
  }

  if (!vacancy) {
    return (
      <div className="w-full h-[500px] flex flex-col justify-center items-center">
        <p className="text-gray-500 text-xl">{t("vacancy_not_found")}</p>
        <Link to="/" className="mt-4 text-[#006674] underline">
          {t("back_to_home")}
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="max-w-7xl mx-auto opacity-0 animate-[show_1s_forwards] bg-white flex flex-col gap-3 pb-10">
        {/* Рендеринг глобального компонента Modal */}
        {modalMessage && (
          <Modal
            title={modalMessage.title}
            text={modalMessage.text}
            success={modalMessage.success}
            ok={modalMessage.ok}
            onClose={() => setModalMessage(null)}
          />
        )}
        
        {/* ХЕДЕР */}
        <div className="relative w-full h-[230px] max-md:h-[120px] border-b-4 border-b-[#006674]">
          {imageUrls.background ? (
            <img
              src={imageUrls.background}
              alt="background"
              className="size-full object-cover"
            />
          ) : (
            <div className="size-full bg-gray-400 flex justify-center items-center text-3xl text-white">
              No Background
            </div>
          )}

          {/* Логотип */}
          <div className="absolute top-44 max-md:top-20 left-6 z-10">
            <div className="w-[160px] h-[160px] max-md:w-[130px] max-md:h-[130px] rounded-xl shadow-2xl bg-white flex items-center justify-center border">
              {imageUrls.logo ? (
                <img
                  src={imageUrls.logo}
                  className="w-full h-full object-contain rounded-xl"
                  alt="company logo"
                />
              ) : (
                <span className="text-gray-500">No Logo</span>
              )}
            </div>
          </div>
        </div>

        {/* Отступ под логотип */}
        <div className="h-24 max-md:h-20" />

        {/* Управление редактированием */}
        {userType === "company" && (
          <div className="px-6 flex justify-end">
            {isEditing ? (
              <button 
                onClick={handleSaveChanges} 
                disabled={isSaving}
                className="bg-green-600 text-white px-6 py-2 rounded-xl flex items-center gap-2 hover:bg-green-700 transition disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faSave} /> {isSaving ? "..." : t("saveChanges")}
              </button>
            ) : (
              <button 
                onClick={() => setIsEditing(true)} 
                className="bg-[#006674] text-white px-6 py-2 rounded-xl flex items-center gap-2 hover:bg-[#005662] transition"
              >
                <FontAwesomeIcon icon={faEdit} /> {t("EDIT")}
              </button>
            )}
          </div>
        )}

        {/* Название вакансии */}
        <div className="px-6">
          {isEditing ? (
            <input 
              name="workname"
              value={vacancy.workname}
              onChange={handleEditChange}
              className="text-4xl max-sm:text-2xl font-semibold p-2 border rounded-xl w-full outline-none focus:border-[#006674]"
            />
          ) : (
            <h2 className="text-4xl max-sm:text-2xl font-semibold">{vacancy.workname}</h2>
          )}
        </div>

        <div className="px-6 pb-6 flex flex-col gap-3">
          <Link to={`/ViewCompanyPage/${vacancy.user_id}`} className="text-2xl hover:underline text-[#006674] w-fit">
            {vacancy.companyName}
          </Link>
          
          <h3 className="text-xl text-gray-600 flex items-center gap-2">
            ID: {vacancy.id}
            <button onClick={handleCopyId} className="hover:text-[#006674]"><FontAwesomeIcon icon={faCopy} /></button>
            {copied && <span className="text-sm text-[#006674]">{t("copied")}</span>}
          </h3>

          <hr className="border-[#006674] my-3" />

          {/* Блок основных параметров */}
          <div className="grid grid-cols-1 gap-4 max-w-2xl">
            {/* Отрасль */}
            <div>
              <span className="font-semibold text-[#006674] block mb-1">{t("industry")}:</span>
              {isEditing ? (
                <select 
                  name="industry1" 
                  value={vacancy.industry1 || ""} 
                  onChange={handleEditChange} 
                  className="p-2 border rounded-xl w-full bg-white outline-none focus:border-[#006674] cursor-pointer"
                >
                  <option value="" disabled>{t("choose_industry") || "Выберите отрасль"}</option>
                  {INDUSTRIES.map((ind, idx) => (
                    <option key={idx} value={ind.value}>{ind.value}</option>
                  ))}
                </select>
              ) : (
                <p>{t(`industries.${vacancy.industry1}`) || vacancy.industry1}</p>
              )}
            </div>

            {/* Место работы */}
            <div>
              <span className="font-semibold text-[#006674] block mb-1">{t("jobplace")}:</span>
              {isEditing ? (
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={vacancy.workplace === t("remote")}
                      onChange={handleRemoteToggle}
                      className="w-5 h-5 accent-[#006674] cursor-pointer"
                    />
                    <span>{t("remote")}</span>
                  </label>
                  
                  {vacancy.workplace !== t("remote") && (
                    <input
                      type="text"
                      value={vacancy.workplaceAddress || ""}
                      onChange={handleAddressChange}
                      placeholder={t("concreteaddres") || "Введите адрес"}
                      className="p-2 border rounded-xl w-full outline-none focus:border-[#006674]"
                    />
                  )}
                </div>
              ) : (
                <p>
                  {vacancy.workplace === t("remote") 
                    ? t("remote") 
                    : (vacancy.workplaceAddress || vacancy.workplace || t("not_specified"))}
                </p>
              )}
            </div>

            {/* Тип занятости (տեսակ) */}
            <div>
              <span className="font-semibold text-[#006674] block mb-1">{t("EmploymentType")}:</span>
              {isEditing ? (
                <select 
                  name="type" 
                  value={vacancy.type || ""} 
                  onChange={handleEditChange} 
                  className="p-2 border rounded-xl w-full bg-white outline-none focus:border-[#006674] cursor-pointer"
                >
                  <option value="" disabled>{t("choose type") || "Выберите тип занятости"}</option>
                  {EMPLOYMENT_TYPES.map((type, idx) => (
                    <option key={idx} value={type.key}>{type.label}</option>
                  ))}
                </select>
              ) : (
                <p>{t(vacancy.type) || vacancy.type}</p>
              )}
            </div>

            {/* График работы (դրույք) */}
            <div>
              <span className="font-semibold text-[#006674] block mb-1">{t("EmploymentTime")}:</span>
              {isEditing ? (
                <select 
                  name="rate" 
                  value={vacancy.rate || ""} 
                  onChange={handleEditChange} 
                  className="p-2 border rounded-xl w-full bg-white outline-none focus:border-[#006674] cursor-pointer"
                >
                  <option value="" disabled>{t("chooseemploymenttime") || "Выберите график работы"}</option>
                  {RATE_TYPES.map((rate, idx) => (
                    <option key={idx} value={rate.key}>{rate.label}</option>
                  ))}
                </select>
              ) : (
                <p>{t(vacancy.rate) || vacancy.rate}</p>
              )}
            </div>

            {/* Опыт работы */}
            <div>
              <span className="font-semibold text-[#006674] block mb-1">{t("Experience")}:</span>
              {isEditing ? (
                <select 
                  name="experience" 
                  value={vacancy.experience || ""} 
                  onChange={handleEditChange} 
                  className="p-2 border rounded-xl w-full bg-white outline-none focus:border-[#006674] cursor-pointer"
                >
                  <option value="" disabled>{t("Chooseanexperience") || "Выберите опыт работы"}</option>
                  {EXPERIENCE_OPTIONS.map((exp, idx) => (
                    <option key={idx} value={exp.key}>{exp.label}</option>
                  ))}
                </select>
              ) : (
                <p>{t(vacancy.experience) || vacancy.experience}</p>
              )}
            </div>

            {/* Зарплата и валюта */}
            <div>
              <span className="font-semibold text-[#006674] block mb-1">{t("Salary")}:</span>
              {isEditing ? (
                <div className="flex gap-2">
                  <input 
                    name="salary" 
                    type="number" 
                    value={vacancy.salary || ""} 
                    onChange={handleEditChange} 
                    className="p-2 border rounded-xl w-full outline-none focus:border-[#006674]" 
                  />
                  <select 
                    name="currency" 
                    value={vacancy.currency || "AMD"} 
                    onChange={handleEditChange} 
                    className="p-2 border rounded-xl w-32 bg-white outline-none focus:border-[#006674] cursor-pointer"
                  >
                    {CURRENCIES.map((cur, idx) => (
                      <option key={idx} value={cur}>{cur}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <p>
                  {vacancy.salary === "Contract" || vacancy.salary === "Договорная" ? t("Contract") : vacancy.salary}{" "}
                  {vacancy.currency}
                </p>
              )}
            </div>
          </div>

          <hr className="border-[#006674] my-3" />

          {/* Описание */}
          <div className="mt-4">
            <h2 className="text-xl font-bold text-[#006674] mb-2">{t("DESCRIPTION")}</h2>
            {isEditing ? (
              <textarea 
                name="description"
                value={vacancy.description || ""}
                onChange={handleEditChange}
                className="p-3 border rounded-xl w-full h-40 outline-none focus:border-[#006674]"
              />
            ) : (
              <p className="text-gray-700 whitespace-pre-line">{vacancy.description || t("no_description")}</p>
            )}
          </div>

          {/* Динамические списки */}
          {[
            { title: t("RESPONSIBILITIES"), key: "responsibilities" },
            { title: t("REQUIREMENTSANDESSENTIALSKILLS"), key: "requirements" },
            { title: t("TERMS AND BENEFITS"), key: "conditions" },
          ].map((block) => (
            <div key={block.key} className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-[#006674]">{block.title}</h2>
                {isEditing && (
                  <button 
                    onClick={() => addListItem(block.key)}
                    className="text-sm bg-[#006674] text-white p-2 rounded-xl flex items-center justify-center hover:bg-[#005662] transition"
                  >
                    <FontAwesomeIcon icon={faPlus} /> 
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="flex flex-col gap-2">
                  {vacancy[block.key]?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input 
                        value={item}
                        onChange={(e) => handleListChange(block.key, idx, e.target.value)}
                        className="p-2 border rounded-xl w-full outline-none focus:border-[#006674]"
                      />
                      <button 
                        onClick={() => removeListItem(block.key, idx)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="list-disc px-6 mt-2">
                  {vacancy[block.key]?.map((item, idx) => (
                    <li key={idx} className="text-gray-800">{item}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Статистика / Форма отклика */}
          {userType === "company" ? (
            <div className="mt-10 p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <h2 className="text-2xl font-bold text-[#006674] mb-4">{t("statistics")}</h2>
              <p>{t("views")}: {vacancy.views}</p>
              <p>{t("applications")}: {vacancy.responses}</p>
            </div>
          ) : (
            <div className="mt-10 p-6 bg-white border rounded-2xl shadow-lg">
              <h2 className="text-2xl font-bold text-[#006674] mb-4">{t("APPLY")}</h2>
              {submitStatus && (
                <div className={`p-3 mb-4 rounded ${submitStatus.type === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                  {t(submitStatus.message)}
                </div>
              )}
              <form onSubmit={handleSubmitResponse} className="flex flex-col gap-4">
                <input name="name" placeholder={t("name")} value={responseForm.name} onChange={handleInputChange} className="p-3 border rounded-xl outline-none focus:border-[#006674]" required />
                <input name="email" type="email" placeholder={t("email")} value={responseForm.email} onChange={handleInputChange} className="p-3 border rounded-xl outline-none focus:border-[#006674]" required />
                <input name="phone" placeholder={t("phone")} value={responseForm.phone} onChange={handleInputChange} className="p-3 border rounded-xl outline-none focus:border-[#006674]" required />
                <textarea name="message" placeholder={t("coverlater")} value={responseForm.message} onChange={handleInputChange} className="p-3 border rounded-xl h-32 outline-none focus:border-[#006674]" required />
                
                <div className="flex items-center gap-4">
                  <label htmlFor="file" className="bg-[#006674] text-white px-6 py-2 rounded-xl cursor-pointer">{t("UploadCV")}</label>
                  <input type="file" id="file" onChange={handleFileChange} className="hidden" />
                  {responseForm.resume && <span className="text-sm text-gray-500">{responseForm.resume.name}</span>}
                </div>

                <button type="submit" disabled={isSubmitting} className="bg-[#006674] text-white py-3 px-10 rounded-xl hover:bg-[#005662] disabled:opacity-50">
                  {isSubmitting ? "..." : t("APPLY")}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Похожие вакансии */}
      {similarVacancies.length > 0 && !isEditing && (
        <div className="max-w-7xl mx-auto mt-10 px-6">
          <h2 className="text-2xl font-bold mb-6">{t("simularjobs")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {similarVacancies.map(vac => (
              <div key={vac.id} className="border rounded-xl p-4 shadow-sm hover:shadow-md transition">
                <Link to={`/vacancy/${vac.id}`} className="text-[#006674] font-bold text-lg">{vac.workname}</Link>
                <p className="text-gray-500">{vac.companyName}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ViewVacancie;